import { useMemo } from "react";
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import { BaseWidget } from "./BaseWidget";
import { ColorControl } from "../controls/ColorControl";
import { FirestoreSelectControl } from "../controls/FirestoreSelectControl";
import useSettings from "../../../../hooks/Settings";
import useSharedCollection from "../../../../firebase/useSharedCollection";
import PersonasRoutesVisualization from "../../../dataVisualizations/PersonasRoutesVisualization";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import isTemplateMode from "./_shared/isTemplateMode";
import { recordInLocation } from '../../../../helpers/locations/locationFilter'
import usePersonaLocations from '../../../../hooks/usePersonaLocations';

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// PersonasRoutesWidget — bar chart of personas grouped by their assigned
// PersonaRoute. Mirrors the dashboard's PersonaRoutesCard; widget exposes
// the location filter through the sidebar instead of an in-card chip.


/**
 * Shape personas + routes into the bar-chart datum array.
 *
 * @param {Array<{ID: string, Title?: string, Position?: number}>} routes
 * @param {Array<{RouteID?: string, LocationID?: string}>} personas
 * @param {string} locationFilter - '' = all
 * @returns {Array<{name: string, routeId: string, value: {total: number}}>}
 */
const buildRouteData = (routes, personas, locationFilter) => {
    const routeList = Array.isArray(routes) ? routes : [];
    const personaList = Array.isArray(personas) ? personas : [];

    const inLocation = locationFilter
        ? personaList.filter((p) => recordInLocation(p, locationFilter))
        : personaList;

    const countsByRouteId = inLocation.reduce((acc, p) => {
        const key = p?.RouteID;
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
    }, {});

    return routeList.map((route) => ({
        name: route.Title || '—',
        routeId: route.ID,
        value: { total: countsByRouteId[route.ID] || 0 },
    }));
};

/**
 * @extends {BaseWidget}
 */
export class PersonasRoutesWidget extends BaseWidget {

    getName() { return 'Trajecten'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    countsAllPersonas() { return true; }
    getIcon() { return <RouteOutlinedIcon />; }

    getControls() {
        return {
            'locationFilter': new FirestoreSelectControl({
                label: 'Locatie',
                description: 'Filter op locatie. Leeg = alle locaties.',
                firestore: {
                    collection: 'Locations',
                    valueField: 'docid',
                    labelField: 'Title',
                    orderBy: 'Position',
                    includeAll: true,
                },
            }),
            'graphColor': new ColorControl({
                label: 'Staafkleur',
                description: 'Kleur van de staven. Leeg = gebruik de tenant-primaire kleur.',
                defaultValue: '',
            }),
        };
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * @param {object} props
 * @param {string} [props.locationFilter]
 * @param {string} [props.graphColor]
 * @returns {JSX.Element}
 */
const Renderer = ({ locationFilter, graphColor }) => {
    const { personasOn, PrimaryColor } = useSettings();
    const isTemplate = isTemplateMode();
    const routes = useSharedCollection('PersonaRoutes');
    const personas = usePersonaLocations();

    const data = useMemo(
        () => buildRouteData(routes, personas, locationFilter),
        [routes, personas, locationFilter]
    );

    if (isTemplate) {
        return <ModulePlaceholder mode="template" />;
    }
    if (personasOn !== 'flex') {
        return <ModulePlaceholder mode="disabled" moduleLabel="Medewerkers" />;
    }

    const color = graphColor || PrimaryColor;

    return (
        <div className="personas-widget-chart">
            <PersonasRoutesVisualization data={data} color={color} height={'100%'} />
        </div>
    );
};

export { buildRouteData };
